import { Loader, MultiSelect, Radio, Stack } from '@mantine/core';
import { SearchDatabaseOptions } from '@scaleup/models';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import {
  useCreateSupplierRequestMutation,
  useGetUserQuery,
} from '../../../api';
import { SUPPLIER_PREFERENCES } from '../../../constants';
import { LDFeatureFlags, useLDFeatureFlag } from '../../../hooks';
import { Button, Conditional, EditableInputV2 } from '../../../ui';
import { useSupplierRequestWizardContext } from './context';

export const Review = () => {
  const {
    supplierRequestInput,
    setCurrentStep,
    handleChangeValue,
    flow,
    saveDraftLoading,
    saveSupplierRequestDraft,
  } = useSupplierRequestWizardContext();
  const [createSupplierRequest, { isLoading: isCreateSupplierRequestLoading }] =
    useCreateSupplierRequestMutation();

  const { data: user, isLoading: userLoading } = useGetUserQuery();
  const shouldShowDatabaseOptions = Boolean(
    process.env.NEXT_PUBLIC_CUSTOMER_DATABASE_SEARCH_AVAILABLE === 'true'
  );

  if (userLoading) {
    return <Loader />;
  }

  const handleSubmit = async () => {
    await createSupplierRequest(supplierRequestInput);
    setCurrentStep('CONFIRMATION');
  };

  const getDatabasePreference = () => {
    const { databasePreference } = supplierRequestInput;
    const companyOnly =
      databasePreference && databasePreference === 'USE_COMPANY_DATABASE_ONLY';

    if (companyOnly) {
      return `${user?.org?.orgName || 'Your company'}'s database`;
    }

    return `${user?.org?.orgName || 'Your company'}'s database and ScaleUp's database`;
  };

  const getDiversityCategories = () => {
    if (
      supplierRequestInput.supplierPreference &&
      supplierRequestInput.supplierPreference.length > 0
    ) {
      return supplierRequestInput.supplierPreference.join(', ');
    }

    return 'No Preference';
  };

  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-8 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold text-text-primary">
            Review Request Details
          </span>
          <span className="text-base font-normal text-text-secondary">
            Please review the details of your supplier request and make changes
            as needed before submitting.
          </span>
        </div>
        <div className="flex flex-col gap-4">
          <>
            <Conditional condition={flow !== 'upload'}>
              <EditableInputV2
                icon="/icons/document.svg"
                label="Requirements"
                value={supplierRequestInput.description}
              >
                <textarea
                  className="w-full rounded-2xl bg-gray-100  p-3 sm:min-h-[150px] md:min-h-[250px] lg:min-h-[175px]"
                  placeholder="Enter buying needs..."
                  value={supplierRequestInput.description}
                  onChange={(e) =>
                    handleChangeValue('description', e.target.value)
                  }
                ></textarea>
              </EditableInputV2>
            </Conditional>

            <EditableInputV2
              icon="/icons/user-star.svg"
              label="Preferred Diversity Categories"
              value={getDiversityCategories()}
            >
              <MultiSelect
                data={SUPPLIER_PREFERENCES}
                className="w-3/5"
                value={supplierRequestInput.supplierPreference}
                onChange={(vals: string[]) =>
                  handleChangeValue('supplierPreference', vals)
                }
              />
            </EditableInputV2>
            {shouldShowDatabaseOptions && (
              <EditableInputV2
                icon="/icons/gear-2.svg"
                label="Preferred Database"
                value={getDatabasePreference()}
              >
                <Radio.Group
                  defaultValue="USE_COMPANY_DATABASE_ONLY"
                  onChange={(val: string) => {
                    handleChangeValue('databasePreference', val);
                  }}
                >
                  <Stack>
                    <Radio
                      value={SearchDatabaseOptions.USE_COMPANY_DATABASE_ONLY}
                      label={`${user?.org?.orgName || 'Your company'}'s database`}
                    />
                    <Radio
                      value={
                        SearchDatabaseOptions.USE_COMPANY_AND_SCALEUP_DATABASE
                      }
                      label={`${user?.org?.orgName || 'Your company'}'s database and ScaleUp's database`}
                    />
                  </Stack>
                </Radio.Group>
              </EditableInputV2>
            )}
          </>
        </div>
        <div className="flex justify-between pt-8">
          <div>
            <Button
              text="Back"
              variant={
                isCreateSupplierRequestLoading ? 'disabled' : 'secondary'
              }
              prefixIcon={<IconArrowLeft />}
              onClick={() => setCurrentStep('PREFERENCES')}
            />
          </div>

          <div className="flex gap-4">
            {useLDFeatureFlag(
              LDFeatureFlags.SUPPLIER_REQUEST_SAVE_AS_DRAFT,
              false
            ) && (
              <Button
                text="Save as Draft"
                loading={saveDraftLoading}
                prefixIcon="/icons/draft.svg"
                variant={
                  isCreateSupplierRequestLoading ? 'disabled' : 'secondary'
                }
                theme="light"
                onClick={saveSupplierRequestDraft}
              />
            )}

            <Button
              text="Continue"
              variant="primary"
              loading={isCreateSupplierRequestLoading}
              suffixIcon={<IconArrowRight />}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
